<template>
  <div class="auth-processing-container">
    <div class="processing-card">
      <div class="logo-section">
        <img :src="require('@/assets/images/icon.png')" alt="Codeway Logo" class="logo">
        <h2 class="welcome-text">Welcome to Config Panel</h2>
      </div>
      
      <div class="loading-animation">
        <div class="spinner">
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
        </div>
      </div>
      
      <div class="processing-text">
        <h3>Setting up your workspace...</h3>
        <div class="processing-steps">
          
          <div class="step" :class="{ 'completed': currentStep > 1, 'current': currentStep === 1 }">
            <div class="step-icon">
              <span v-if="currentStep > 1">✓</span>
              <span v-else-if="currentStep === 1">⟳</span>
              <span v-else>1</span>
            </div>
            <div class="step-content">
              <div class="step-title">Verifying Authentication</div>
              <div class="step-description">{{ step1Description }}</div>
            </div>
          </div>
          
          <div class="step" :class="{ 'completed': currentStep > 2, 'current': currentStep === 2 }">
            <div class="step-icon">
              <span v-if="currentStep > 2">✓</span>
              <span v-else-if="currentStep === 2">⟳</span>
              <span v-else>2</span>
            </div>
            <div class="step-content">
              <div class="step-title">Loading User Profile</div>
              <div class="step-description">{{ step2Description }}</div>
            </div>
          </div>
          
          <div class="step" :class="{ 'completed': currentStep > 3, 'current': currentStep === 3 }">
            <div class="step-icon">
              <span v-if="currentStep > 3">✓</span>
              <span v-else-if="currentStep === 3">⟳</span>
              <span v-else>3</span>
            </div>
            <div class="step-content">
              <div class="step-title">Loading Applications</div>
              <div class="step-description">{{ step3Description }}</div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import router from '../../router';
import tokenManagerService from '../../services/tokenManager';

export default {
  name: 'AuthProcessingScreen',
  data() {
    return {
      currentStep: 0,
      step1Description: 'Waiting to start...',
      step2Description: 'Pending...',
      step3Description: 'Pending...',
    };
  },
  async mounted() {
    // Start immediately on mount
    await this.processAuthentication();
  },
  methods: {
    async processAuthentication() {
      try {
        // === STEP 1: Verify Token ===
        this.currentStep = 1;
        this.step1Description = 'Validating session...';
        
        // UX: Wait at least 600ms so user sees the step, but also check token
        await Promise.all([
          tokenManagerService.getValidToken(),
          this.minDelay(600)
        ]).then(([token]) => {
          if (!token) throw new Error("No valid token found");
        });
        
        this.step1Description = 'Session verified.';

        // === STEP 2: Load User Profile ===
        this.currentStep = 2;
        this.step2Description = 'Fetching user details...';
        
        await Promise.all([
          this.$store.dispatch("setUser"),
          this.minDelay(800) 
        ]);
        
        this.step2Description = 'Profile loaded.';

        // === STEP 3: Load Applications ===
        this.currentStep = 3;
        this.step3Description = 'Configuring dashboard...';
        
        // Only fetch if we don't have them (optimization), but keep animation
        const apps = this.$store.getters.apps;
        const needsFetch = !apps || (Array.isArray(apps) && apps.length === 0);
        
        debugger
        await Promise.all([
          needsFetch ? this.$store.dispatch("fetchApps") : Promise.resolve(),
          this.minDelay(800)
        ]);

        this.step3Description = 'Ready!';
        this.currentStep = 4; // All complete

        // === FINALIZE: Redirect ===
        await this.minDelay(300); // Tiny pause to show all checkmarks
        this.handleRedirect();

      } catch (error) {
        console.error('❌ Auth processing failed:', error);
        // Clear potential bad state
        sessionStorage.removeItem('okta_redirect_path');
        router.replace({ path: '/signin', query: { error: 'login_failed' } });
      }
    },

    handleRedirect() {
      // 1. Check Query Param (passed from LoginCallback)
      let target = this.$route.query.redirect;

      // 2. Check Session Storage (passed from Router Guard)
      if (!target) {
        target = sessionStorage.getItem('okta_redirect_path');
        sessionStorage.removeItem('okta_redirect_path');
      }

      // 3. Validate Target (Security: Prevent Open Redirects)
      if (target && target.startsWith('/')) {
        console.log("🚀 Redirecting to:", target);
        router.replace(target);
      } else {
        console.log("🚀 Redirecting to Dashboard");
        router.replace('/');
      }
    },
    
    // Helper to ensure animations don't flash too fast
    minDelay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
  },
};
</script>

<style scoped>
/* I kept your original CSS exactly as is. 
   It was well written and works perfectly with this logic.
*/
.auth-processing-container {
  min-height: 100vh;
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(#1e1e2f, #1e1e24);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: 0;
  padding: 0;
}

.processing-card {
  background: #27283b;
  border-radius: 16px;
  padding: 50px 40px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  text-align: center;
  animation: slideUp 0.6s ease-out;
  width: 100%;
  max-width: 500px;
  border: 1px solid #39395c;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.logo-section {
  margin-bottom: 30px;
}

.logo {
  width: 80px;
  height: 80px;
  margin-bottom: 16px;
  border-radius: 12px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
}

.welcome-text {
  color: #ffffff;
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.loading-animation {
  margin: 30px 0;
}

.spinner {
  position: relative;
  width: 60px;
  height: 60px;
  margin: 0 auto;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-top: 3px solid #8374cf;
  border-radius: 50%;
  animation: spin 1.2s linear infinite;
}

.spinner-ring:nth-child(2) {
  width: 80%;
  height: 80%;
  top: 10%;
  left: 10%;
  border-top-color: #ba54f5;
  animation-delay: -0.4s;
}

.spinner-ring:nth-child(3) {
  width: 60%;
  height: 60%;
  top: 20%;
  left: 20%;
  border-top-color: #e14eca;
  animation-delay: -0.8s;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.processing-text h3 {
  color: #ffffff;
  font-size: 18px;
  font-weight: 500;
  margin: 0 0 30px 0;
}

.processing-steps {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.step {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  border-radius: 12px;
  background: #1e1e2f;
  transition: all 0.3s ease;
  border-left: 4px solid transparent;
  border: 1px solid #39395c;
}

.step.current {
  background: #2a2a3e;
  border-left-color: #8374cf;
  box-shadow: 0 2px 8px rgba(131, 116, 207, 0.2);
}

.step.completed {
  background: #1e2a1e;
  border-left-color: #00f2c3;
  border-color: #00f2c3;
}

.step-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.step:not(.completed):not(.current) .step-icon {
  background: #39395c;
  color: #778ca3;
}

.step.current .step-icon {
  background: #8374cf;
  color: white;
  animation: pulse 1.5s infinite;
}

.step.completed .step-icon {
  background: #00f2c3;
  color: #1e1e2f;
  animation: stepComplete 0.6s ease-out;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

@keyframes stepComplete {
  0% {
    transform: scale(1);
    background: #39395c;
  }
  50% {
    transform: scale(1.3);
    background: #00f2c3;
  }
  100% {
    transform: scale(1);
    background: #00f2c3;
  }
}

.step-content {
  flex: 1;
  text-align: left;
}

.step-title {
  font-weight: 600;
  color: #ffffff;
  font-size: 14px;
  margin-bottom: 4px;
}

.step-description {
  color: #778ca3;
  font-size: 12px;
  line-height: 1.4;
}

@media (max-width: 480px) {
  .processing-card {
    padding: 40px 30px;
    margin: 10px;
    max-width: 350px;
  }
  
  .welcome-text {
    font-size: 20px;
  }
  
  .logo {
    width: 60px;
    height: 60px;
  }
  
  .step {
    padding: 12px;
    gap: 12px;
  }
  
  .step-icon {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }
}

html, body {
  margin: 0;
  padding: 0;
  height: 100%;
  overflow: hidden;
}
</style>